import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import { CheckCircle, Download, Mail, FileText } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '@/utils/api';

const API = API_URL;

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      const foundOrder = response.data.find(o => o.id === orderId);
      if (foundOrder) {
        setOrder(foundOrder);
      }
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadGuide = () => {
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = `${API}/measuring-guide`;
    link.download = 'HPD_Measuring_Guide.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SEO pageId="order-confirmation" defaultTitle="Order Confirmation | House Plan Direct" defaultDescription="Your House Plan Direct floor plan order confirmation." noIndex />
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading order details...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SEO pageId="order-confirmation" defaultTitle="Order Confirmation | House Plan Direct" defaultDescription="Your House Plan Direct floor plan order confirmation." noIndex />
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <p className="text-gray-600">Order not found</p>
          <Link to="/" className="mt-4 inline-block">
            <Button>Return Home</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const extras = [];
  if (order.add_colour) extras.push('Colour');
  if (order.add_3d) extras.push('3D');
  if (order.add_furniture) extras.push('Furniture');
  if (order.add_symbols) extras.push('Symbols & Key');

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO pageId="order-confirmation" defaultTitle="Order Confirmation | House Plan Direct" defaultDescription="Your House Plan Direct floor plan order confirmation." noIndex />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Message */}
        <div className="text-center mb-8" data-testid="confirmation-header">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
          <p className="text-lg text-gray-600">Thank you for your order. We'll get started right away.</p>
        </div>

        {/* Order Details Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 mb-6" data-testid="order-details">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Details</h2>
            <p className="text-gray-600">A confirmation email has been sent to <strong>{order.email}</strong></p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Order Information</h3>
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-gray-600">Order Number</dt>
                  <dd className="font-semibold text-gray-900" data-testid="order-number">{order.order_number}</dd>
                </div>
                <div>
                  <dt className="text-gray-600">Property Type</dt>
                  <dd className="font-semibold text-gray-900">{order.property_type}</dd>
                </div>
                <div>
                  <dt className="text-gray-600">Number of Rooms</dt>
                  <dd className="font-semibold text-gray-900">{order.room_count}</dd>
                </div>
                {extras.length > 0 && (
                  <div>
                    <dt className="text-gray-600">Extras</dt>
                    <dd className="font-semibold text-gray-900">{extras.join(', ')}</dd>
                  </div>
                )}
              </dl>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Payment Summary</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-600">Base Plan</dt>
                  <dd className="font-semibold text-gray-900">£{order.base_price.toFixed(2)}</dd>
                </div>
                {order.extras_price > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Extras</dt>
                    <dd className="font-semibold text-gray-900">£{order.extras_price.toFixed(2)}</dd>
                  </div>
                )}
                {order.guide_price > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Guide</dt>
                    <dd className="font-semibold text-gray-900">£{order.guide_price.toFixed(2)}</dd>
                  </div>
                )}
                <div className="flex justify-between pt-3 mt-3 border-t border-gray-200">
                  <dt className="font-semibold text-gray-900">Total Paid</dt>
                  <dd className="font-bold text-blue-600 text-lg" data-testid="total-paid">£{order.total_price.toFixed(2)}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>

        {/* Measuring Guide */}
        <div className="bg-blue-50 rounded-xl p-6 border border-blue-200 mb-6" data-testid="guide-section">
          <div className="flex items-start space-x-4">
            <FileText className="h-8 w-8 text-blue-600 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Measuring Guidance PDF</h3>
              <p className="text-gray-700 mb-4">
                <strong>The free measuring guide PDF has been attached to your order confirmation email.</strong> You can also download it here for future reference.
              </p>
              <Button
                onClick={handleDownloadGuide}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                data-testid="download-guide-btn"
              >
                <Download className="mr-2 h-4 w-4" /> Download Guide
              </Button>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 mb-6" data-testid="next-steps">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">What Happens Next?</h2>
          <div className="space-y-4">
            {order.uploaded_files && order.uploaded_files.length > 0 ? (
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">We have received your sketch</p>
                  <p className="text-sm text-gray-600">Our team will begin processing your floor plan.</p>
                </div>
              </div>
            ) : (
              <div className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">Please email your sketch</p>
                  <p className="text-sm text-gray-600">
                    Send your sketch or photos to{' '}
                    <a href="mailto:web@houseplandirect.com" className="text-blue-600 hover:underline">
                      web@houseplandirect.com
                    </a>
                    {' '}quoting order number: <strong>{order.order_number}</strong>
                  </p>
                </div>
              </div>
            )}
            <div className="flex items-start space-x-3">
              <FileText className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-gray-900">We'll process your order</p>
                <p className="text-sm text-gray-600">Our team will create your professional CAD floor plan.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Mail className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-gray-900">Receive your floor plan</p>
                <p className="text-sm text-gray-600">You'll receive your completed plans by email in PDF, JPEG, and DWG formats.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Support */}
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 text-center" data-testid="support-section">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Need Help?</h3>
          <p className="text-gray-600 mb-4">
            If you have any questions about your order, please contact us at{' '}
            <a href="mailto:web@houseplandirect.com" className="text-blue-600 hover:underline">
              web@houseplandirect.com
            </a>
          </p>
          <Link to="/">
            <Button variant="outline" data-testid="return-home-btn">Return to Home</Button>
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default OrderConfirmation;
